import api from "@/utils/api";

/**
 * Get school-wise leads summary for admin
 * Similar to /v1/reports/leadsreports endpoint
 */
export const getAdminLeadsSummary = async (params: {
  fromDate: string;
  toDate: string;
  clientId: number;
  includeDuplicates?: number;
}) => {
  const response = await api.req({
    url: "/reports/admin/leads-summary",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get school insights report
 * Similar to /v1/reports/insightsclient-report endpoint
 */
export const getAdminSchoolInsights = async (params: {
  from_date: string;
  to_date: string;
  slate_id: number;
}) => {
  const response = await api.req({
    url: "/reports/admin/school-insights",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get list of schools for dropdown
 */
export const getAdminSchools = async () => {
  const response = await api.req({
    url: "/reports/admin/schools",
    method: api.Method.GET
  });
  return response;
};

/**
 * Get admin source/status matrix
 * Uses admin_source, admin_status from crm_lead_admin_tracking
 */
export const getAdminSourceStatusMatrix = async (params: {
  from_date: string;
  to_date: string;
}) => {
  const response = await api.req({
    url: "/reports/admin/source-status-matrix",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get admin sub-source breakdown
 */
export const getAdminSubSourceBreakdown = async (params: {
  from_date: string;
  to_date: string;
}) => {
  const response = await api.req({
    url: "/reports/admin/sub-source-breakdown",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get admin grade distribution
 */
export const getAdminGradeDistribution = async (params: {
  from_date: string;
  to_date: string;
}) => {
  const response = await api.req({
    url: "/reports/admin/grade-distribution",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get Facebook Ads campaign performance by school
 */
export const getAdminFbReports = async (params: {
  from_date: string;
  to_date: string;
  sort_by?: string;
  sort_order?: string;
}) => {
  const response = await api.req({
    url: "/reports/admin/fb-campaign-performance",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get Facebook Form Mappings
 */
export const getFbFormMappings = async () => {
  const response = await api.req({
    url: "/reports/admin/fb-form-mappings",
    method: api.Method.GET
  });
  return response;
};

/**
 * Create Facebook Form Mapping
 */
export const createFbFormMapping = async (params: {
  form_id: string;
  client_id: number;
}) => {
  const response = await api.req({
    url: "/reports/admin/fb-form-mappings",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Delete Facebook Form Mapping
 */
export const deleteFbFormMapping = async (id: number) => {
  const response = await api.req({
    url: `/reports/admin/fb-form-mappings/${id}`,
    method: api.Method.DELETE
  });
  return response;
};

/**
 * Get Google Ads Campaign Mappings
 */
export const getGoogleCampaignMappings = async () => {
  return await api.req({ url: "/reports/admin/google-campaign-mappings", method: api.Method.GET });
};

/**
 * Create Google Ads Campaign Mapping
 */
export const createGoogleCampaignMapping = async (params: {
  campaign_id: string;
  campaign_name: string;
  client_id: number;
}) => {
  return await api.req({ url: "/reports/admin/google-campaign-mappings", method: api.Method.POST, body: params });
};

/**
 * Delete Google Ads Campaign Mapping
 */
export const deleteGoogleCampaignMapping = async (id: number) => {
  return await api.req({ url: `/reports/admin/google-campaign-mappings/${id}`, method: api.Method.DELETE });
};

/**
 * Get Google Ads campaigns from the Ads account (for the mapping UI dropdown)
 */
export const getGoogleAdsCampaigns = async () => {
  return await api.req({ url: "/reports/admin/google-ads-campaigns", method: api.Method.GET });
};

/**
 * Get Ad Level Report - Campaign > AdSet > Ad hierarchy with status breakdown
 */
export const getAdminAdLevelReport = async (params: {
  from_date: string;
  to_date: string;
  slate_id: number;
}) => {
  const response = await api.req({
    url: "/reports/admin/ad-level-report",
    method: api.Method.POST,
    body: params
  });
  return response;
};

/**
 * Get Master Report - combined School Summary and FB Ads data
 */
export const getAdminMasterReport = async (params: {
  from_date: string;
  to_date: string;
  includeDuplicates?: number;
  slate_ids?: number[];
}) => {
  const response = await api.req({
    url: "/reports/admin/master-report",
    method: api.Method.POST,
    body: params
  });
  return response;
};
